import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useMutation } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import Labels from "../labels/Labels";

async function cloneUser({ sourceUsername, targetUsername, targetEmail }) {
  return HttpService.getAxiosClient().post(
    window.API_URL + "/employer/user/clone",
    { sourceUsername, targetUsername, targetEmail }
  );
}

export default function CloneUser({ open, setOpen }) {
  const [sourceUsername, setSourceUsername] = useState("");
  const [targetUsername, setTargetUsername] = useState("");
  const [targetEmail, setTargetEmail] = useState("");
  const [validationError, setValidationError] = useState("");
  const [feedback, setFeedback] = useState({
    open: false,
    type: "success",
    message: "",
  });

  const cloneUserMutation = useMutation(cloneUser, {
    onSuccess: () => {
      setOpen(false);
      setSourceUsername("");
      setTargetUsername("");
      setTargetEmail("");
      setValidationError("");
      setFeedback({
        open: true,
        type: "success",
        message: Labels.getLabel(
          "cloneUser.feedback.success",
          "User cloned successfully. A temporary password was sent to the target email address."
        ),
      });
    },
    onError: (error) => {
      setFeedback({
        open: true,
        type: "error",
        message:
          error?.response?.data?.message ||
          error?.response?.data ||
          Labels.getLabel(
            "cloneUser.feedback.error",
            "Unable to clone user. Please try again."
          ),
      });
    },
  });

  const handleClose = () => {
    if (cloneUserMutation.isLoading) {
      return;
    }
    setOpen(false);
    setValidationError("");
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const source = sourceUsername.trim();
    const target = targetUsername.trim();

    const email = targetEmail.trim();

    if (!source || !target || !email) {
      setValidationError(
        Labels.getLabel(
          "cloneUser.validation.required",
          "Source username, target username, and target email are required."
        )
      );
      return;
    }

    if (source.toLowerCase() === target.toLowerCase()) {
      setValidationError(
        Labels.getLabel(
          "cloneUser.validation.different",
          "Source and target usernames must be different."
        )
      );
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setValidationError(
        Labels.getLabel(
          "cloneUser.validation.email",
          "Enter a valid target email address."
        )
      );
      return;
    }

    setValidationError("");
    cloneUserMutation.mutate({
      sourceUsername: source,
      targetUsername: target,
      targetEmail: email,
    });
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <Box component="form" onSubmit={handleSubmit}>
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ContentCopyIcon color="primary" />
            {Labels.getLabel("cloneUser.title", "Clone RMC User")}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {Labels.getLabel(
                "cloneUser.description",
                "Copy the source user's profile, access attributes, and roles to a new username."
              )}
            </Typography>
            <TextField
              autoFocus
              fullWidth
              required
              margin="normal"
              label={Labels.getLabel(
                "cloneUser.sourceUsername",
                "Source username"
              )}
              value={sourceUsername}
              onChange={(event) => setSourceUsername(event.target.value)}
              disabled={cloneUserMutation.isLoading}
            />
            <TextField
              fullWidth
              required
              margin="normal"
              label={Labels.getLabel(
                "cloneUser.targetUsername",
                "Target username"
              )}
              value={targetUsername}
              onChange={(event) => setTargetUsername(event.target.value)}
              disabled={cloneUserMutation.isLoading}
            />
            <TextField
              fullWidth
              required
              type="email"
              margin="normal"
              label={Labels.getLabel("cloneUser.targetEmail", "Target email")}
              value={targetEmail}
              onChange={(event) => setTargetEmail(event.target.value)}
              disabled={cloneUserMutation.isLoading}
            />
            {validationError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {validationError}
              </Alert>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
              {Labels.getLabel(
                "cloneUser.credentialsNote",
                "A temporary password will be generated and sent to the target email address."
              )}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={cloneUserMutation.isLoading}>
              {Labels.getLabel("cloneUser.cancel", "Cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={
                cloneUserMutation.isLoading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <ContentCopyIcon />
                )
              }
              disabled={cloneUserMutation.isLoading}
            >
              {Labels.getLabel("cloneUser.submit", "Clone user")}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={() => setFeedback({ ...feedback, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={feedback.type}
          onClose={() => setFeedback({ ...feedback, open: false })}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </>
  );
}
